import { useState, useEffect, useRef } from 'react';
import { eggReadinessOptions, popularEggTypes } from '../../data/eggData';
import EggReadinessSelector from './EggReadinessSelector';
import EggSettingsSelector from './EggSettingsSelector';
import TimerDisplay from './TimerDisplay';
import { cn } from '../../utils/cn';
import { ArrowUp } from 'lucide-react';

export type EggTemperature = 'refrigerated' | 'room';
export type EggSize = 'small' | 'medium' | 'large';
export type CookingMethod = 'boiling' | 'cold';

export interface EggSettings {
  temperature: EggTemperature;
  size: EggSize;
  cookingMethod: CookingMethod;
}

const EggTimer = () => {
  const [selectedReadiness, setSelectedReadiness] = useState(eggReadinessOptions[2]); // Default to medium
  const [settings, setSettings] = useState<EggSettings>({
    temperature: 'refrigerated',
    size: 'medium',
    cookingMethod: 'boiling',
  });
  
  const [timerSeconds, setTimerSeconds] = useState(0);
  const [isTimerActive, setIsTimerActive] = useState(false);
  const [initialSeconds, setInitialSeconds] = useState(0);
  const [isCompleted, setIsCompleted] = useState(false);
  
  const timerRef = useRef<number | null>(null);
  const startTimeRef = useRef<number | null>(null);

  // Calculate timer duration based on settings
  const calculateTimerDuration = () => {
    let duration = selectedReadiness.durationSeconds;
    
    if (settings.temperature === 'refrigerated') {
      duration += 30;
    }
    
    if (settings.size === 'small') {
      duration -= 30;
    } else if (settings.size === 'large') {
      duration += 30;
    }
    
    if (settings.cookingMethod === 'cold') {
      duration += 180;
    }
    
    return duration;
  };

  // Update timer
  useEffect(() => {
    if (!isTimerActive) return;

    const updateTimer = () => {
      if (!startTimeRef.current) return;

      const elapsed = Date.now() - startTimeRef.current;
      const remaining = Math.max(0, initialSeconds - Math.floor(elapsed / 1000));

      if (remaining === 0) {
        setTimerSeconds(0);
        setIsCompleted(true);
        setIsTimerActive(false);
        if (timerRef.current) {
          cancelAnimationFrame(timerRef.current);
          timerRef.current = null;
        }
      } else {
        setTimerSeconds(remaining);
        timerRef.current = requestAnimationFrame(updateTimer);
      }
    };

    timerRef.current = requestAnimationFrame(updateTimer);

    return () => {
      if (timerRef.current) {
        cancelAnimationFrame(timerRef.current);
      }
    };
  }, [isTimerActive, initialSeconds]);

  // Start timer
  const startTimer = () => {
    const duration = calculateTimerDuration();
    setInitialSeconds(duration);
    setTimerSeconds(duration);
    setIsTimerActive(true);
    setIsCompleted(false);
    startTimeRef.current = Date.now();
  };

  // Stop timer
  const stopTimer = () => {
    setIsTimerActive(false);
    setTimerSeconds(0);
    setInitialSeconds(0);
    setIsCompleted(false);
    startTimeRef.current = null;
    if (timerRef.current) {
      cancelAnimationFrame(timerRef.current);
      timerRef.current = null;
    }
  };

  // Handle readiness selection
  const handleReadinessSelect = (readiness: typeof eggReadinessOptions[0]) => {
    setSelectedReadiness(readiness);
    if (isTimerActive || isCompleted) {
      stopTimer();
    }
  };

  // Handle settings change
  const handleSettingsChange = (newSettings: Partial<EggSettings>) => {
    setSettings(prev => ({ ...prev, ...newSettings }));
    if (isTimerActive || isCompleted) {
      stopTimer();
    }
  };

  // Handle popular egg type selection
  const handlePopularEggSelect = (eggType: typeof popularEggTypes[0]) => {
    const readiness = eggReadinessOptions.find(r => r.id === eggType.readinessId);
    if (readiness) {
      setSelectedReadiness(readiness);
      if (isTimerActive || isCompleted) {
        stopTimer();
      }
    }
  };

  return (
    <div className="flex flex-col items-center max-w-4xl mx-auto px-4 py-8">
      <h2 className="font-display text-2xl md:text-3xl text-gray-800 font-bold mb-6 text-center">
        Perfect Eggs Every Time
      </h2>
      
      <div className="w-full mb-8">
        <EggReadinessSelector 
          options={eggReadinessOptions} 
          selected={selectedReadiness} 
          onSelect={handleReadinessSelect}
          disabled={isTimerActive}
        />
      </div>

      <div className="w-full mb-8">
        <h3 className="font-display text-xl text-gray-800 font-semibold mb-4 text-center">
          Popular Settings
        </h3>
        <div className="flex flex-wrap justify-center gap-2">
          {popularEggTypes.map((eggType) => (
            <button
              key={eggType.id}
              onClick={() => handlePopularEggSelect(eggType)}
              disabled={isTimerActive}
              className={cn(
                "px-4 py-2 rounded-full text-sm transition-colors",
                "border hover:bg-primary-50 hover:border-primary-300",
                selectedReadiness.id === eggType.readinessId
                  ? "border-primary-500 bg-primary-50 text-primary-700"
                  : "border-gray-200 bg-white text-gray-700",
                isTimerActive && "opacity-60 cursor-not-allowed"
              )}
              title={eggType.description}
            >
              {eggType.name}
            </button>
          ))}
        </div>
      </div>
      
      <div className="w-full mb-8">
        <EggSettingsSelector 
          settings={settings} 
          onChange={handleSettingsChange}
          disabled={isTimerActive}
        />
      </div>

      <div className="flex justify-center mb-8">
        <ArrowUp className="w-6 h-6 text-[#FFD700] animate-bounce" />
      </div>
      
      <div className="w-full max-w-md mb-12">
        <TimerDisplay 
          seconds={timerSeconds}
          initialSeconds={initialSeconds}
          isActive={isTimerActive}
          isCompleted={isCompleted}
          onStart={startTimer}
          onReset={stopTimer}
          readiness={selectedReadiness}
        />
      </div>
    </div>
  );
};

export default EggTimer;